/**
 * useDiagramEditMode - Diagram AI Editing State Hook
 * 
 * Manages AI editing state for wiring diagrams AND flowcharts (Mermaid diagrams),
 * matching the document pattern:
 * - Enable/disable AI editing mode
 * - Query for existing linked conversation or create new
 * - Link diagram artifact to conversation for AI capability derivation
 * 
 * Both wiring diagrams and flowcharts use artifactType: 'diagram'
 * Pattern matches useEditMode.ts for documents.
 */

import { useCallback, useState } from 'react';
import { logger } from '../utils/logger';
import { conversationsApi } from '../services/api';

export interface DiagramForEdit {
  diagramId: string;
  projectId: string;
  title: string;
}

export interface FlowchartForEdit {
  mermaidDiagramId: string;
  projectId: string;
  title: string;
}

interface UseDiagramEditModeDeps {
  // Link-based architecture: auto-create linked conversation on edit
  createConversation: (title?: string) => Promise<string>;
  switchConversation: (id: string, addToVisibleTabs?: boolean) => Promise<void>;
  linkArtifact: (
    artifactType: 'document' | 'ssa' | 'diagram' | 'plan',
    artifactId: string,
    artifactName: string,
    projectId: string,
    conversationId?: string
  ) => Promise<void>;
  updateConversationTitle: (conversationId: string, newTitle: string) => void;
  
  // Diagram state management
  setActiveDiagramId: (id: string | null) => void;
}

interface UseDiagramEditModeReturn {
  // Wiring diagram editing
  /** Enable AI editing mode for a wiring diagram, auto-linking to AI conversation */
  enableDiagramEdit: (diagram: DiagramForEdit) => Promise<void>;
  /** Disable AI editing mode for wiring diagram (link persists in backend) */
  disableDiagramEdit: () => void;
  /** Currently active wiring diagram being edited with AI */
  activeDiagramEditId: string | null;
  
  // Flowchart (Mermaid) editing
  /** Enable AI editing mode for a flowchart, auto-linking to AI conversation */
  enableFlowchartEdit: (flowchart: FlowchartForEdit) => Promise<void>;
  /** Disable AI editing mode for flowchart (link persists in backend) */
  disableFlowchartEdit: () => void;
  /** Currently active flowchart being edited with AI */
  activeFlowchartEditId: string | null;
  
  /** Loading state while linking (shared for both diagram types) */
  isLinking: boolean;
}

export function useDiagramEditMode(deps: UseDiagramEditModeDeps): UseDiagramEditModeReturn {
  const {
    createConversation,
    switchConversation,
    linkArtifact,
    updateConversationTitle,
    setActiveDiagramId,
  } = deps;
  
  // Track which diagram is in AI edit mode
  const [activeDiagramEditId, setActiveDiagramEditId] = useState<string | null>(null);
  const [activeFlowchartEditId, setActiveFlowchartEditId] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  /**
   * Enable AI editing mode for a diagram
   * Queries backend for existing linked conversation, or creates a new one
   * The link persists in the database - robust across hard reloads
   */
  const enableDiagramEdit = useCallback(async (diagram: DiagramForEdit) => {
    const { diagramId, projectId, title } = diagram;
    
    logger.debug('[useDiagramEditMode] enableDiagramEdit called:', { diagramId, title });
    
    if (!diagramId || !projectId) {
      logger.error('[useDiagramEditMode] diagramId and projectId are required');
      return;
    }
    
    setIsLinking(true);
    
    try {
      // Set the active diagram for AI context
      setActiveDiagramId(diagramId);
      setActiveDiagramEditId(diagramId);
      
      // Query backend for existing conversation linked to this diagram
      // This is robust across hard reloads - link is stored in database
      const result = await conversationsApi.getByArtifact('diagram', diagramId);
      const existingConversations = result?.conversations || [];
      
      logger.debug('[useDiagramEditMode] Backend query result:', { 
        found: existingConversations.length,
        diagramId 
      });
      
      // Use friendly title, matching document pattern
      const diagramTitle = title || diagramId;
      const expectedTitle = `${diagramTitle} - AI Session`;
      
      if (existingConversations.length > 0) {
        // Reuse the most recent linked conversation
        const mostRecent = existingConversations[0];
        logger.debug('[useDiagramEditMode] Found existing linked conversation:', mostRecent.conversationId);
        
        // Update title if it doesn't match (e.g., was created with ID instead of friendly name)
        if (mostRecent.title !== expectedTitle) {
          logger.debug('[useDiagramEditMode] Updating conversation title:', mostRecent.title, '→', expectedTitle);
          updateConversationTitle(mostRecent.conversationId, expectedTitle);
        }
        
        // Pass true to add to visible tabs (conversation may have been hidden)
        await switchConversation(mostRecent.conversationId, true);
      } else {
        // Create new linked conversation with friendly title
        const newConversationId = await createConversation(expectedTitle);
        
        if (newConversationId) {
          await linkArtifact('diagram', diagramId, diagramTitle, projectId, newConversationId);
          logger.debug('[useDiagramEditMode] Created new linked conversation:', newConversationId);
          
          // CRITICAL: Switch to the new conversation to properly load its state from backend
          // This ensures conversation links, tab colors, and link indicators are properly synchronized
          await switchConversation(newConversationId, true);
        }
      }
    } catch (error) {
      logger.error('[useDiagramEditMode] Failed to find/create linked conversation:', error);
      // AI editing still technically works, just without proper linking
    } finally {
      setIsLinking(false);
    }
  }, [createConversation, switchConversation, linkArtifact, updateConversationTitle, setActiveDiagramId]);

  /**
   * Disable AI editing mode for wiring diagram
   * Note: The link persists in the backend database
   * This allows reusing the same conversation next time the user enables editing
   * The link is only broken when the user closes the conversation tab (X)
   */
  const disableDiagramEdit = useCallback(() => {
    logger.debug('[useDiagramEditMode] Disabling wiring diagram AI edit mode');
    
    // Clear active diagram state
    setActiveDiagramId(null);
    setActiveDiagramEditId(null);
    
    logger.debug('[useDiagramEditMode] Wiring diagram AI edit mode disabled (conversation link persists)');
  }, [setActiveDiagramId]);

  /**
   * Enable AI editing mode for a flowchart (Mermaid diagram)
   * Queries backend for existing linked conversation, or creates a new one
   * Uses artifactType: 'diagram' (shared with wiring diagrams)
   * Does NOT set activeDiagramId (that's for wiring diagrams only)
   */
  const enableFlowchartEdit = useCallback(async (flowchart: FlowchartForEdit) => {
    const { mermaidDiagramId, projectId, title } = flowchart;
    
    logger.debug('[useDiagramEditMode] enableFlowchartEdit called:', { mermaidDiagramId, title });
    
    if (!mermaidDiagramId || !projectId) {
      logger.error('[useDiagramEditMode] mermaidDiagramId and projectId are required');
      return;
    }
    
    setIsLinking(true);
    
    try {
      // Set the active flowchart for tracking (NOT setActiveDiagramId - that's for wiring)
      setActiveFlowchartEditId(mermaidDiagramId);
      
      // Query backend for existing conversation linked to this flowchart
      // Uses 'diagram' artifact type (shared with wiring diagrams)
      const result = await conversationsApi.getByArtifact('diagram', mermaidDiagramId);
      const existingConversations = result?.conversations || [];
      
      logger.debug('[useDiagramEditMode] Flowchart backend query result:', { 
        found: existingConversations.length,
        mermaidDiagramId 
      });
      
      // Use friendly title, matching document pattern
      const flowchartTitle = title || mermaidDiagramId;
      const expectedTitle = `${flowchartTitle} - AI Session`;
      
      if (existingConversations.length > 0) {
        // Reuse the most recent linked conversation
        const mostRecent = existingConversations[0];
        logger.debug('[useDiagramEditMode] Found existing linked flowchart conversation:', mostRecent.conversationId);
        
        // Update title if it doesn't match
        if (mostRecent.title !== expectedTitle) {
          logger.debug('[useDiagramEditMode] Updating flowchart conversation title:', mostRecent.title, '→', expectedTitle);
          updateConversationTitle(mostRecent.conversationId, expectedTitle);
        }
        
        // Pass true to add to visible tabs (conversation may have been hidden)
        await switchConversation(mostRecent.conversationId, true);
      } else {
        // Create new linked conversation with friendly title
        const newConversationId = await createConversation(expectedTitle);
        
        if (newConversationId) {
          // Link with 'diagram' artifact type (shared with wiring diagrams)
          await linkArtifact('diagram', mermaidDiagramId, flowchartTitle, projectId, newConversationId);
          logger.debug('[useDiagramEditMode] Created new linked flowchart conversation:', newConversationId);
          
          // CRITICAL: Switch to the new conversation to properly load its state from backend
          // This ensures conversation links, tab colors, and link indicators are properly synchronized
          await switchConversation(newConversationId, true);
        }
      }
    } catch (error) {
      logger.error('[useDiagramEditMode] Failed to find/create linked flowchart conversation:', error);
    } finally {
      setIsLinking(false);
    }
  }, [createConversation, switchConversation, linkArtifact, updateConversationTitle]);

  /**
   * Disable AI editing mode for flowchart
   * Note: The link persists in the backend database
   */
  const disableFlowchartEdit = useCallback(() => {
    logger.debug('[useDiagramEditMode] Disabling flowchart AI edit mode');
    setActiveFlowchartEditId(null);
    logger.debug('[useDiagramEditMode] Flowchart AI edit mode disabled (conversation link persists)');
  }, []);

  return {
    // Wiring diagram
    enableDiagramEdit,
    disableDiagramEdit,
    activeDiagramEditId,
    // Flowchart
    enableFlowchartEdit,
    disableFlowchartEdit,
    activeFlowchartEditId,
    // Shared
    isLinking,
  };
}
